import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PageHero from "../components/PageHero";
import axios from "axios";
import {
  PoundSterling, Phone, Mail, AlertTriangle, CheckCircle2, Clock,
  Plus, Filter, RefreshCw, ChevronRight, Search, XCircle, FileText, Pencil,
} from "lucide-react";
import { Button } from "../components/ui/button";
import SmartClientMatterSearch from "../components/SmartClientMatterSearch";
import EditCreditControlModal from "../components/EditCreditControlModal";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { getUserRole, ROLES } from "../config/roleConfig";

const STAGE_COLORS = {
  invoice_issued: { bg: "bg-teal/20", text: "text-teal", label: "Invoice Issued" },
  reminder_1: { bg: "bg-yellow-500/20", text: "text-yellow-400", label: "Reminder 1" },
  call_1: { bg: "bg-yellow-500/20", text: "text-yellow-400", label: "Call 1" },
  reminder_2: { bg: "bg-status-amber/20", text: "text-status-amber", label: "Reminder 2" },
  call_2: { bg: "bg-status-amber/20", text: "text-status-amber", label: "Call 2" },
  reminder_3: { bg: "bg-status-red/20", text: "text-status-red", label: "Reminder 3" },
  call_3: { bg: "bg-status-red/20", text: "text-status-red", label: "Call 3" },
  lba_sent: { bg: "bg-gray-700/60", text: "text-gray-300", label: "LBA Sent" },
  proceedings: { bg: "bg-gray-700/60", text: "text-gray-300", label: "Proceedings" },
};

const STATUS_COLORS = {
  active: { bg: "bg-blue-500/20", text: "text-blue-400" },
  part_paid: { bg: "bg-status-amber/20", text: "text-status-amber" },
  paid: { bg: "bg-teal/20", text: "text-teal" },
  disputed: { bg: "bg-status-red/20", text: "text-status-red" },
  lba_sent: { bg: "bg-gray-600/40", text: "text-gray-300" },
  referred: { bg: "bg-purple-500/20", text: "text-purple-400" },
  written_off: { bg: "bg-gray-700/40", text: "text-gray-500" },
  payment_arrangement: { bg: "bg-teal/20", text: "text-teal" },
  pending_match: { bg: "bg-yellow-500/20", text: "text-yellow-400" },
};

function headers() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const email = user.user_email || user.email || user.ms_email || sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "";
  return {
    "X-Bonsai-User-Id": sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "",
    "X-User-Email": email,
  };
}

function StageBadge({ stage }) {
  const c = STAGE_COLORS[stage] || { bg: "bg-gray-600/40", text: "text-gray-400", label: stage };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.text}`} data-testid={`stage-badge-${stage}`}>
      {c.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || { bg: "bg-gray-600/40", text: "text-gray-400" };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.text}`}>
      {status?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
    </span>
  );
}

function OverdueBadge({ days }) {
  if (days <= 7) return <span className="text-teal text-xs font-medium">{days}d</span>;
  if (days <= 14) return <span className="text-yellow-400 text-xs font-medium">{days}d</span>;
  if (days <= 21) return <span className="text-status-amber text-xs font-medium">{days}d</span>;
  if (days <= 28) return <span className="text-status-red text-xs font-medium">{days}d</span>;
  return <span className="text-gray-300 font-bold text-xs">{days}d</span>;
}

function SummaryCards({ summary }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="cc-summary-cards">
      <div className="bg-navy-card border border-white/10 rounded-lg p-4">
        <div className="text-xs text-gray-400 mb-1">Active Cases</div>
        <div className="text-2xl font-bold text-white">{summary.active_count} <span className="text-xs text-gray-500 font-normal">({summary.total_count || 0} total)</span></div>
      </div>
      <div className="bg-navy-card border border-white/10 rounded-lg p-4">
        <div className="text-xs text-gray-400 mb-1">Total Outstanding</div>
        <div className="text-2xl font-bold text-teal">
          £{(summary.total_outstanding || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
        </div>
      </div>
      <div className="bg-navy-card border border-white/10 rounded-lg p-4">
        <div className="text-xs text-gray-400 mb-1">Pre-LBA</div>
        <div className="text-2xl font-bold text-status-amber">
          {(summary.by_stage?.reminder_3 || 0) + (summary.by_stage?.call_3 || 0)}
        </div>
      </div>
      <div className="bg-navy-card border border-white/10 rounded-lg p-4">
        <div className="text-xs text-gray-400 mb-1">LBA / Proceedings</div>
        <div className="text-2xl font-bold text-status-red">
          {(summary.by_stage?.lba_sent || 0) + (summary.by_stage?.proceedings || 0)}
        </div>
      </div>
    </div>
  );
}

const STATUS_BREAKDOWN_ORDER = [
  "active", "part_paid", "pending_match", "payment_arrangement",
  "paid", "pending_payment", "referred", "lba_sent",
  "disputed", "invoice_requested", "written_off",
];

const DEFAULT_STATUS_FILTER = "active,part_paid,pending_match,payment_arrangement";

// BM-H611: how many records the status filter is hiding, from the same
// every-record counts the breakdown banner uses. Shown whenever a status
// filter is applied and it hides anything, so nothing seems to vanish.
function HiddenByStatusNotice({ summary, statusFilter, onShowAll }) {
  const byStatus = summary?.by_status || {};
  if (!statusFilter || Object.keys(byStatus).length === 0) return null;
  const shown = new Set(statusFilter.split(","));
  const hidden = Object.entries(byStatus)
    .filter(([k]) => !shown.has(k))
    .reduce((acc, [, n]) => acc + (n || 0), 0);
  if (hidden === 0) return null;
  return (
    <div className="text-xs text-gray-400 mb-3" data-testid="cc-hidden-by-status">
      {hidden} record{hidden === 1 ? " is" : "s are"} hidden by the status filter
      (paid, referred and others).{" "}
      <button
        onClick={onShowAll}
        className="text-teal hover:underline"
        data-testid="cc-show-all-statuses"
      >Show all</button>
    </div>
  );
}

function StatusBreakdownBanner({ summary }) {
  const byStatus = summary?.by_status || {};
  const known = new Set(STATUS_BREAKDOWN_ORDER);
  const orderedKeys = [
    ...STATUS_BREAKDOWN_ORDER.filter(k => byStatus[k]),
    ...Object.keys(byStatus).filter(k => !known.has(k) && byStatus[k]),
  ];
  if (orderedKeys.length === 0) return null;
  const total = summary?.total_records ?? orderedKeys.reduce((acc, k) => acc + (byStatus[k] || 0), 0);
  return (
    <div
      className="text-xs text-gray-400 mb-3 truncate"
      data-testid="cc-status-breakdown"
      title="Counts every non-deleted record, regardless of current filter"
    >
      <span className="text-gray-500 mr-2">All records by status ({total} total):</span>
      {orderedKeys.map((k, i) => (
        <span key={k}>
          {i > 0 && <span className="text-gray-600 mx-2">·</span>}
          <span className="text-white font-medium">{byStatus[k]}</span>{" "}
          <span className="text-gray-400">{k.replace(/_/g, " ")}</span>
        </span>
      ))}
    </div>
  );
}

// ── BM-H124 (29 Jul 2026) ────────────────────────────────────────────
// Full record-plus-invoice modal for May: LawBillity-originated invoices
// don't have a CaseMinder payment-request preceding them, so she needs
// to key the whole thing manually. SmartClientMatterSearch autofills
// client_name, matter_title and fee earner from SharePoint. Optional
// PDF upload attaches the invoice to the record so Fiona / any fee
// earner reviewing the credit control tracker can see it.

const MAX_PDF_BYTES = 15 * 1024 * 1024;

async function _fileToBase64(file) {
  const buf = await file.arrayBuffer();
  // btoa on raw binary strings is unsafe for large files (each char
  // must be latin-1); chunk into 32k blocks to build the binary string
  // safely across browsers.
  const bytes = new Uint8Array(buf);
  let bin = "";
  const CHUNK = 32 * 1024;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(
      null,
      // eslint-disable-next-line prefer-spread
      bytes.subarray(i, Math.min(i + CHUNK, bytes.length)),
    );
  }
  return btoa(bin);
}

// BM-H127 -- source-mode toggle for the New Record modal. "lawbillity"
// is the pre-BM-H127 manual-entry path (matter search + free-text
// invoice details). "invoice_request" is the new path: May picks a
// pending invoice_request submission from a dropdown, we prefill
// everything the request already carries, and the backend stamps
// credit_control_id back onto that submission on create.
const SOURCE_LAWBILLITY = "lawbillity";
const SOURCE_INVOICE_REQUEST = "invoice_request";

function CreateRecordModal({ open, onClose, onCreated }) {
  const [sourceMode, setSourceMode] = useState(SOURCE_LAWBILLITY);
  const [form, setForm] = useState({
    matter_ref: "", client_name: "", fee_earner: "", matter_title: "",
    invoice_number: "", amount: "", due_date: "", invoice_date: "",
    notes: "",
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfError, setPdfError] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // BM-H127 -- eligible invoice_request rows for the picker. Loaded
  // lazily the first time the user switches to that mode so the
  // LawBillity path stays zero-cost.
  const [eligibleRequests, setEligibleRequests] = useState([]);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [eligibleError, setEligibleError] = useState("");
  const [selectedRequestId, setSelectedRequestId] = useState("");

  const update = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const loadEligibleRequests = useCallback(async () => {
    setEligibleLoading(true);
    setEligibleError("");
    try {
      const resp = await axios.get(
        `${API}/credit-control/eligible-invoice-requests`,
        { headers: headers() },
      );
      setEligibleRequests(resp?.data?.rows || []);
    } catch (err) {
      const msg = errorMessage(err, "Failed to load pending invoice requests.");
      setEligibleError(String(msg));
      setEligibleRequests([]);
    } finally {
      setEligibleLoading(false);
    }
  }, []);

  // Lazy-load the picker list the first time the user flips to that
  // mode after opening the modal.
  useEffect(() => {
    if (!open) return;
    if (sourceMode === SOURCE_INVOICE_REQUEST && eligibleRequests.length === 0 && !eligibleLoading && !eligibleError) {
      loadEligibleRequests();
    }
  }, [open, sourceMode, eligibleRequests.length, eligibleLoading, eligibleError, loadEligibleRequests]);

  // Reset transient state whenever the modal closes so the next open
  // starts clean (no stale error/selection from a previous session).
  useEffect(() => {
    if (open) return;
    setSourceMode(SOURCE_LAWBILLITY);
    setForm({
      matter_ref: "", client_name: "", fee_earner: "", matter_title: "",
      invoice_number: "", amount: "", due_date: "", invoice_date: "",
      notes: "",
    });
    setPdfFile(null);
    setPdfError("");
    setSubmitError("");
    setSelectedRequestId("");
    setEligibleRequests([]);
    setEligibleError("");
  }, [open]);

  if (!open) return null;

  const handleFilePick = (evt) => {
    setPdfError("");
    const file = evt.target.files && evt.target.files[0];
    if (!file) {
      setPdfFile(null);
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      setPdfError("File too large. Maximum 15 MB.");
      setPdfFile(null);
      evt.target.value = "";
      return;
    }
    setPdfFile(file);
  };

  const handleSelectRequest = (requestId) => {
    setSelectedRequestId(requestId);
    const chosen = eligibleRequests.find(r => r.id === requestId);
    if (!chosen) {
      // "Choose a pending request..." placeholder -- wipe prefilled fields
      setForm(p => ({
        ...p,
        matter_ref: "", client_name: "", fee_earner: "", matter_title: "",
        invoice_number: "", amount: "",
      }));
      return;
    }
    setForm(p => ({
      ...p,
      matter_ref:     chosen.matter_reference || "",
      client_name:    chosen.client_name || "",
      fee_earner:     chosen.fee_earner || "",
      matter_title:   chosen.matter_title || "",
      invoice_number: chosen.invoice_request_number || "",
      // fixed_fee_amount is a string on the wire (or empty for WIP);
      // parseFloat + toString normalises "1500.00" and empty stays empty.
      amount:         chosen.fixed_fee_amount ? String(parseFloat(chosen.fixed_fee_amount) || chosen.fixed_fee_amount) : "",
    }));
  };

  const canSubmit = (() => {
    if (!form.matter_ref || !form.client_name) return false;
    if (sourceMode === SOURCE_INVOICE_REQUEST && !selectedRequestId) return false;
    return true;
  })();

  const handleCreate = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setSubmitError("");
    try {
      // BM-H127 -- the record origin flips with the source mode.
      // LawBillity path is unchanged from BM-H124. Invoice-request path
      // records the CC line as caseminder-originated so downstream
      // reporting doesn't misclassify it as a paper invoice.
      const recordOrigin = sourceMode === SOURCE_INVOICE_REQUEST ? "caseminder" : "lawbillity";
      const invoiceLine = form.invoice_number
        ? {
            invoice_number: form.invoice_number,
            amount_outstanding: parseFloat(form.amount) || 0,
            due_date: form.due_date,
            origin: recordOrigin,
          }
        : null;

      if (invoiceLine && pdfFile) {
        invoiceLine.invoice_pdf_b64 = await _fileToBase64(pdfFile);
        invoiceLine.invoice_pdf_filename = pdfFile.name;
        invoiceLine.invoice_pdf_content_type = pdfFile.type || "application/pdf";
      }

      await axios.post(
        `${API}/credit-control/create`,
        {
          matter_ref:   form.matter_ref,
          client_name:  form.client_name,
          fee_earner:   form.fee_earner,
          matter_title: form.matter_title,
          invoices:     invoiceLine ? [invoiceLine] : [],
          invoice_date: form.invoice_date,
          due_date:     form.due_date,
          notes:        form.notes,
          origin:       recordOrigin,
          linked_invoice_request_id: sourceMode === SOURCE_INVOICE_REQUEST ? selectedRequestId : "",
        },
        { headers: headers() },
      );
      onCreated();
      onClose();
    } catch (err) {
      console.error("Create failed:", err);
      const msg = errorMessage(err, "Create failed.");
      setSubmitError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="create-cc-modal">
      <div className="bg-navy-card border border-white/10 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-white mb-1">New Credit Control Record</h3>
        <p className="text-xs text-gray-400 mb-4">
          Manual entry for invoices raised outside CaseMinder, or a pending
          invoice request that needs a matching credit-control line.
        </p>

        {/* BM-H127 -- source-mode toggle. Segmented control, not a radio,
            so it reads as "which starting point?" rather than a form field. */}
        <div
          className="grid grid-cols-2 gap-1 p-1 rounded-md bg-navy border border-white/10 mb-4"
          data-testid="cc-create-source-toggle"
          role="tablist"
          aria-label="Source of the credit-control record"
        >
          <button
            type="button"
            role="tab"
            aria-selected={sourceMode === SOURCE_LAWBILLITY}
            data-testid="cc-create-source-lawbillity"
            className={`px-3 py-1.5 text-xs rounded ${sourceMode === SOURCE_LAWBILLITY ? "bg-teal text-black font-semibold" : "text-gray-300 hover:text-white"}`}
            onClick={() => setSourceMode(SOURCE_LAWBILLITY)}
          >
            New invoice from LawBillity
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={sourceMode === SOURCE_INVOICE_REQUEST}
            data-testid="cc-create-source-invoice-request"
            className={`px-3 py-1.5 text-xs rounded ${sourceMode === SOURCE_INVOICE_REQUEST ? "bg-teal text-black font-semibold" : "text-gray-300 hover:text-white"}`}
            onClick={() => setSourceMode(SOURCE_INVOICE_REQUEST)}
          >
            Link to pending invoice request
          </button>
        </div>

        <div className="space-y-4">
          {sourceMode === SOURCE_INVOICE_REQUEST ? (
            <div>
              <label className="text-xs text-gray-400 block mb-1">
                Pending invoice request <span className="text-status-red">*</span>
              </label>
              <select
                value={selectedRequestId}
                onChange={e => handleSelectRequest(e.target.value)}
                disabled={eligibleLoading}
                className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                data-testid="cc-create-invoice-request-picker"
              >
                <option value="">
                  {eligibleLoading
                    ? "Loading pending invoice requests…"
                    : eligibleRequests.length === 0
                      ? "No pending invoice requests"
                      : "Choose a pending invoice request…"}
                </option>
                {eligibleRequests.map(r => {
                  const number = r.invoice_request_number || "(no number)";
                  const ref = r.matter_reference || "(no matter ref)";
                  const client = r.client_name || "(no client)";
                  const fe = r.fee_earner ? ` · ${r.fee_earner}` : "";
                  return (
                    <option key={r.id} value={r.id}>
                      {`${number} · ${ref} · ${client}${fe}`}
                    </option>
                  );
                })}
              </select>
              {eligibleError && (
                <p className="mt-1 text-xs text-status-red" data-testid="cc-create-eligible-error">
                  {eligibleError}
                </p>
              )}
              {selectedRequestId && (
                <p className="mt-1 text-xs text-gray-500">
                  Prefilled from the invoice request. Edit the amount and dates below if the invoice was raised for a different figure.
                </p>
              )}
            </div>
          ) : (
            <div>
              <label className="text-xs text-gray-400 block mb-1">
                Matter reference <span className="text-status-red">*</span>
              </label>
              <SmartClientMatterSearch
                label={null}
                placeholder="Type client name or matter ref…"
                initialValue={form.matter_ref}
                mattersOnly
                darkMode
                onSelect={(result) => {
                  const ref = result?.matter_reference || result?.label || "";
                  update("matter_ref", ref);
                  if (result?.client_name)  update("client_name", result.client_name);
                  if (result?.matter_title) update("matter_title", result.matter_title);
                  if (result?.fee_earner)   update("fee_earner", result.fee_earner);
                }}
              />
            </div>
          )}

          {/* Autofilled fields — still editable in case SharePoint is stale */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Client name</label>
              <input
                type="text" value={form.client_name}
                onChange={e => update("client_name", e.target.value)}
                placeholder="Auto-fills from matter search"
                className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                data-testid="cc-create-client_name"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Fee earner</label>
              <input
                type="text" value={form.fee_earner}
                onChange={e => update("fee_earner", e.target.value)}
                placeholder="Auto-fills from matter search"
                className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                data-testid="cc-create-fee_earner"
              />
            </div>
          </div>

          {/* Invoice section */}
          <div className="pt-2 border-t border-white/10">
            <div className="text-xs uppercase text-gray-500 mb-2">Invoice</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400 block mb-1">Invoice number</label>
                <input
                  type="text" value={form.invoice_number}
                  onChange={e => update("invoice_number", e.target.value)}
                  placeholder="INV-2026-042"
                  className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                  data-testid="cc-create-invoice_number"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Amount outstanding (£)</label>
                <input
                  type="number" step="0.01" value={form.amount}
                  onChange={e => update("amount", e.target.value)}
                  placeholder="1500.00"
                  className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                  data-testid="cc-create-amount"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Invoice date</label>
                <input
                  type="date" value={form.invoice_date}
                  onChange={e => update("invoice_date", e.target.value)}
                  className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                  data-testid="cc-create-invoice_date"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Due date</label>
                <input
                  type="date" value={form.due_date}
                  onChange={e => update("due_date", e.target.value)}
                  className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                  data-testid="cc-create-due_date"
                />
              </div>
            </div>

            {/* PDF upload */}
            <div className="mt-3">
              <label className="text-xs text-gray-400 block mb-1">
                Invoice PDF (optional, max 15 MB)
              </label>
              <input
                type="file" accept="application/pdf,image/*"
                onChange={handleFilePick}
                className="block w-full text-xs text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-teal/20 file:text-teal file:cursor-pointer"
                data-testid="cc-create-invoice_pdf"
              />
              {pdfFile && (
                <p className="mt-1 text-xs text-gray-400 flex items-center gap-1">
                  <FileText className="w-3 h-3" />{pdfFile.name}
                </p>
              )}
              {pdfError && (
                <p className="mt-1 text-xs text-status-red">{pdfError}</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs text-gray-400 block mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => update("notes", e.target.value)}
              rows={2}
              placeholder="Optional context for the credit control team"
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
              data-testid="cc-create-notes"
            />
          </div>

          {submitError && (
            <p className="text-xs text-status-red" data-testid="cc-create-error">
              {submitError}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={saving || !canSubmit}
            className="bg-teal text-black hover:bg-teal-hover"
            data-testid="cc-create-submit"
          >
            {saving ? "Creating…" : "Create Record"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function CreditControl() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({ active_count: 0, total_outstanding: 0, by_stage: {} });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  // BM-H611 (25 Sep 2026, May via Vanessa): the list opens on Active &
  // Part-paid, the records accounts are chasing. It opened on All Statuses
  // from 29 Apr so that records QBO auto-sync flips to paid / referred /
  // pending_payment did not seem to vanish. That concern is met by
  // HiddenByStatusNotice below, which says how many records the filter is
  // hiding and offers "Show all" in one click.
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);
  const [stageFilter, setStageFilter] = useState("");
  // BM-H587: so May Ann can see "all my Payment promised" and a director can
  // see "everything on Hold". Options come from the backend, never hardcoded.
  const [categoryFilter, setCategoryFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [tagOptions, setTagOptions] = useState({ categories: [], owners: [] });
  // BM-H67 — column sorting. sortKey === null means keep the natural
  // order from the backend (which already returns worst-overdue first).
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [showCreate, setShowCreate] = useState(false);
  // BM-H578: which row is being corrected, if any.
  const [editRecordId, setEditRecordId] = useState(null);

  const userObj = JSON.parse(localStorage.getItem("user") || "{}");
  const userEmail = (userObj.user_email || userObj.email || userObj.ms_email || sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "").toLowerCase().trim();
  const userRole = getUserRole(userEmail);
  const isAccounts = userRole === ROLES.ACCOUNTS;
  const isDirector = userRole === ROLES.DIRECTOR || ["liezel@bonsai.law"].includes(userEmail);
  const canWrite = isAccounts || isDirector;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (stageFilter) params.stage = stageFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (ownerFilter) params.owner = ownerFilter;

      const [listRes, summaryRes] = await Promise.all([
        axios.get(`${API}/credit-control/list`, { params, headers: headers() }),
        axios.get(`${API}/credit-control/overdue-summary`, { headers: headers() }),
      ]);
      setRecords(listRes.data.records || []);
      setSummary(summaryRes.data);
    } catch (err) {
      // Surface auth/network errors instead of silently rendering "0 cases /
      // £0.00 / no records found", which is indistinguishable from the
      // legitimate empty state and was the original P0 symptom.
      const status = err?.response?.status;
      const reason = status === 401 || status === 403
        ? "Your session expired. Sign in again to load credit control."
        : errorMessage(err, "Failed to load credit control records.");
      setLoadError(reason);
      setRecords([]);
      setSummary({ active_count: 0, total_outstanding: 0, by_stage: {}, _loadError: reason });
      console.error("Fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, stageFilter, categoryFilter, ownerFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    axios.get(`${API}/credit-control/tag-options`, { headers: headers() })
      .then(res => setTagOptions(res.data || { categories: [], owners: [] }))
      .catch(() => {});
  }, []);
  const categoryLabel = (value) =>
    ((tagOptions.categories || []).find(c => c.value === value) || {}).label || "";

  const filtered = records.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.matter_ref || "").toLowerCase().includes(s)
      || (r.client_name || "").toLowerCase().includes(s)
      || (r.fee_earner || "").toLowerCase().includes(s);
  });

  // BM-H67 — click a column header to sort. Click again to reverse,
  // third click clears the sort. Empty/null values always fall to the
  // bottom regardless of direction so blanks don't clutter the top.
  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null); setSortDir('asc');
  };

  const _sortValue = (r, key) => {
    switch (key) {
      case 'matter_ref':      return (r.matter_ref || '').toLowerCase();
      case 'client_name':     return (r.client_name || '').toLowerCase();
      case 'fee_earner':      return (r.fee_earner || '').toLowerCase();
      case 'invoices':        return Number(r.total_outstanding) || 0;
      case 'due_date':        return r.due_date ? new Date(r.due_date).getTime() : null;
      case 'days_overdue':    return Number(r.days_overdue) || 0;
      case 'escalation_stage':return (r.escalation_stage || '').toLowerCase();
      case 'status':          return (r.status || '').toLowerCase();
      case 'tags':            return categoryLabel(r.category).toLowerCase() || null;
      case 'next_action_due': return r.next_action_due ? new Date(r.next_action_due).getTime() : null;
      default: return null;
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const dir = sortDir === 'asc' ? 1 : -1;
    const isEmpty = (v) => v === null || v === undefined || v === '' ||
                            (typeof v === 'number' && Number.isNaN(v));
    return [...filtered].sort((a, b) => {
      const va = _sortValue(a, sortKey);
      const vb = _sortValue(b, sortKey);
      // blanks always to the bottom
      if (isEmpty(va) && isEmpty(vb)) return 0;
      if (isEmpty(va)) return 1;
      if (isEmpty(vb)) return -1;
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir, tagOptions]);

  const sortIcon = (key) => {
    if (sortKey !== key) return null;
    return sortDir === 'asc' ? ' \u2191' : ' \u2193';
  };

  const sortableTh = (key, label, extra = '') => (
    <th
      className={`px-4 py-3 text-xs text-gray-400 font-medium cursor-pointer select-none hover:text-white transition-colors ${extra}`}
      onClick={() => toggleSort(key)}
      data-testid={`cc-sort-${key}`}
      aria-sort={sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >{label}{sortIcon(key)}</th>
  );

  return (
    <div className="min-h-screen bg-navy text-white p-6" data-testid="credit-control-page">
      {/* Header */}
      <button onClick={() => window.location.href="/"} className="mb-2 text-xs text-slate-400 hover:text-white flex items-center gap-1">← Dashboard</button>
      {/* 27 Oct 2026 system-wide refresh: header band. */}
      <PageHero icon={PoundSterling} title="Credit Control" subtitle="Debt recovery lifecycle management"
        tile="linear-gradient(135deg, #8b5cf6, #ec4899)" testId="cc-hero">
        <Button variant="outline" size="sm" onClick={fetchData} className="text-gray-300 border-white/15" data-testid="cc-refresh" title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
        {canWrite && (
          <Button
            onClick={() => setShowCreate(true)}
            size="sm"
            data-testid="cc-new-record"
          >
            <Plus className="w-4 h-4 mr-1" /> New Record
          </Button>
        )}
      </PageHero>

      {loadError && (
        <div
          className="mb-4 flex items-start gap-2 rounded-lg border border-status-red/40 bg-status-red/10 px-3 py-2 text-sm text-status-red"
          data-testid="cc-load-error"
          role="alert"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Could not load credit control data.</p>
            <p className="text-xs text-status-red/80">{loadError}</p>
          </div>
        </div>
      )}

      <SummaryCards summary={summary} />
      <StatusBreakdownBanner summary={summary} />
      <HiddenByStatusNotice
        summary={summary}
        statusFilter={statusFilter}
        onShowAll={() => setStatusFilter("")}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="cc-filters">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search matter, client, fee earner..."
            className="w-full bg-navy-card border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-gray-500"
            data-testid="cc-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-navy-card border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          data-testid="cc-status-filter"
        >
          <option value="">All Statuses</option>
          <option value="active,part_paid,pending_match,payment_arrangement">Active &amp; Part-paid</option>
          <option value="active">Active only</option>
          <option value="part_paid">Part-paid</option>
          <option value="pending_match">Pending Match</option>
          <option value="payment_arrangement">Payment Arrangement</option>
          <option value="paid">Paid</option>
          <option value="pending_payment">Pending Payment</option>
          <option value="referred">Referred</option>
          <option value="lba_sent">LBA Sent</option>
          <option value="disputed">Disputed</option>
          <option value="invoice_requested">Invoice Requested</option>
          <option value="written_off">Written Off</option>
        </select>
        <select
          value={stageFilter}
          onChange={e => setStageFilter(e.target.value)}
          className="bg-navy-card border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          data-testid="cc-stage-filter"
        >
          <option value="">All Stages</option>
          <option value="invoice_issued">Invoice Issued</option>
          <option value="reminder_1,call_1">Reminder 1 / Call 1</option>
          <option value="reminder_2,call_2">Reminder 2 / Call 2</option>
          <option value="reminder_3,call_3">Reminder 3 / Call 3</option>
          <option value="lba_sent">LBA Sent</option>
          <option value="proceedings">Proceedings</option>
        </select>
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="bg-navy-card border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          data-testid="cc-category-filter"
        >
          <option value="">All Categories</option>
          <option value="untagged">Untagged</option>
          {(tagOptions.categories || []).map(c => (
            <option key={c.value} value={c.value} title={c.help}>{c.label}</option>
          ))}
        </select>
        <select
          value={ownerFilter}
          onChange={e => setOwnerFilter(e.target.value)}
          className="bg-navy-card border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          data-testid="cc-owner-filter"
        >
          <option value="">All Owners</option>
          <option value="unassigned">Unassigned</option>
          {(tagOptions.owners || []).map(o => (
            <option key={o.email} value={o.email}>{o.initials}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-navy-card border border-white/10 rounded-xl overflow-hidden" data-testid="cc-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {sortableTh('matter_ref', 'Matter')}
                {sortableTh('client_name', 'Client')}
                {sortableTh('fee_earner', 'FE')}
                {sortableTh('invoices', 'Invoices', 'text-right')}
                {sortableTh('due_date', 'Due')}
                {sortableTh('days_overdue', 'Overdue', 'text-center')}
                {sortableTh('escalation_stage', 'Stage')}
                {sortableTh('status', 'Status')}
                {/* BM-H611: category and owner (BM-H587) get their own column,
                    not a line of small print under the status. */}
                {sortableTh('tags', 'Tags')}
                {sortableTh('next_action_due', 'Next Action')}
                {/* BM-H578: correction column. Accounts had no way to fix a
                    mis-keyed amount, date or stage from the grid at all. */}
                <th className="px-4 py-3 text-xs text-gray-400 font-medium"></th>
                <th className="px-4 py-3 text-xs text-gray-400 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="12" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan="12" className="px-4 py-8 text-center text-gray-500">No records found</td></tr>
              ) : sorted.map(r => (
                <tr
                  key={r.id}
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                  onClick={() => navigate(`/credit-control/${r.id}`)}
                  data-testid={`cc-row-${r.id}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-white">{r.matter_ref}</td>
                  <td className="px-4 py-3 text-white">{r.client_name}</td>
                  <td className="px-4 py-3 text-gray-400">{r.fee_earner}</td>
                  <td className="px-4 py-3 text-right text-white">
                    {(r.invoices || []).length} / £{(r.total_outstanding || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{r.due_date}</td>
                  <td className="px-4 py-3 text-center"><OverdueBadge days={r.days_overdue} /></td>
                  <td className="px-4 py-3"><StageBadge stage={r.escalation_stage} /></td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 text-xs" data-testid={`cc-tags-${r.id}`}>
                    {r.category && (
                      <span className="px-2 py-0.5 rounded bg-white/10 text-white whitespace-nowrap">
                        {categoryLabel(r.category) || r.category}
                      </span>
                    )}
                    {r.owner_initials && (
                      <span className="ml-1.5 text-gray-400" title="Owner">{r.owner_initials}</span>
                    )}
                    {!r.category && !r.owner_initials && <span className="text-gray-600">-</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{r.next_action_due}</td>
                  <td className="px-4 py-3">
                    {canWrite && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditRecordId(r.id); }}
                        className="text-gray-500 hover:text-teal transition-colors"
                        title="Correct this record"
                        aria-label={`Edit ${r.matter_ref || "record"}`}
                        data-testid={`cc-edit-${r.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3"><ChevronRight className="w-4 h-4 text-gray-500" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CreateRecordModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={fetchData} />
      <EditCreditControlModal
        open={!!editRecordId}
        recordId={editRecordId}
        onClose={() => setEditRecordId(null)}
        onSaved={fetchData}
      />
    </div>
  );
}
